import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { firstValueFrom } from 'rxjs';
import { OfflineOutboxRow, offlineDb } from './offline-db';
import { OfflineStatusService } from './offline-status.service';
import { OfflineBlobService } from './offline-blob.service';
import { User } from '../shared/interfaces/user.interface';

@Injectable({ providedIn: 'root' })
export class OfflineOutboxService {
  private draining = false;

  constructor(
    private http: HttpClient,
    private cookies: CookieService,
    private status: OfflineStatusService,
    private blobs: OfflineBlobService
  ) {}

  async enqueue(
    partial: Omit<OfflineOutboxRow, 'id' | 'createdAt' | 'attempts' | 'idempotencyKey'> & {
      idempotencyKey?: string;
    }
  ): Promise<number> {
    const idempotencyKey =
      partial.idempotencyKey ||
      `${partial.method}:${partial.url}:${partial.entityId || ''}:${Date.now()}:${Math.random()}`;
    const id = await offlineDb.outbox.add({
      ...partial,
      idempotencyKey,
      createdAt: Date.now(),
      attempts: 0,
    });
    await this.status.refreshPending();
    return id as number;
  }

  async pendingCount(): Promise<number> {
    return offlineDb.outbox.count();
  }

  async drain(): Promise<void> {
    if (this.draining || !this.status.isOnline) return;
    this.draining = true;
    this.status.setSyncing(true);
    try {
      const rows = await offlineDb.outbox.orderBy('createdAt').toArray();
      for (const row of rows) {
        if (!this.status.isOnline) break;
        try {
          await this.replayOne(row);
          if (!this.status.isOnline) this.status.markOnline();
          if (row.id != null) await offlineDb.outbox.delete(row.id);
        } catch (err: any) {
          if (row.id != null) {
            await offlineDb.outbox.update(row.id, {
              attempts: (row.attempts || 0) + 1,
              lastError: err?.message || String(err),
            });
          }
          // Network gone — pause retries until connectivity recovers
          if (err?.status === 0) {
            this.status.markOffline();
            break;
          }
          // Reachable HTTP error (incl. auth) — clear stuck offline from WebView
          if (!this.status.isOnline) this.status.markOnline();
          // Stop drain on auth failure so user can re-login
          if (err?.status === 401 || err?.status === 403) break;
        }
      }
    } finally {
      this.draining = false;
      this.status.setSyncing(false);
      await this.status.refreshPending();
    }
  }

  private authHeaders(): HttpHeaders {
    let user: User | undefined;
    const raw = this.cookies.get('ddnsession');
    if (raw) {
      try {
        user = JSON.parse(raw) as User;
      } catch {
        /* ignore */
      }
    }
    return new HttpHeaders({ Authorization: 'Bearer ' + (user?.token || '') });
  }

  private async replayOne(row: OfflineOutboxRow): Promise<void> {
    const headers = this.authHeaders();

    if (row.kind === 'multipart' && row.blobIds?.length) {
      const form = new FormData();
      const body = (row.body || {}) as Record<string, unknown>;
      for (const [k, v] of Object.entries(body)) {
        if (v != null && k !== '_blobField') form.append(k, String(v));
      }
      const field = (body['_blobField'] as string) || 'file';
      for (const blobId of row.blobIds) {
        const file = await this.blobs.toFile(blobId);
        if (file) form.append(field, file, file.name);
      }
      await firstValueFrom(this.http.post(row.url, form, { headers }));
      return;
    }

    if (row.kind === 'gcs-put' && row.blobIds?.[0]) {
      const blobRow = await offlineDb.blobs.get(row.blobIds[0]);
      if (!blobRow) throw new Error('Missing blob for GCS put');
      await firstValueFrom(
        this.http.put(row.url, blobRow.bytes, {
          headers: new HttpHeaders({
            'Content-Type': blobRow.mimeType || 'application/octet-stream',
          }),
          withCredentials: false,
        })
      );
      return;
    }

    // notify / api
    if (row.method === 'DELETE') {
      await firstValueFrom(
        this.http.delete(row.url, { headers, body: row.body ?? {} })
      );
      return;
    }
    if (row.method === 'PUT') {
      await firstValueFrom(this.http.put(row.url, row.body ?? {}, { headers }));
      return;
    }
    // POST default (update/add/notify)
    await firstValueFrom(this.http.post(row.url, row.body ?? {}, { headers }));
  }
}
